import { useRef } from 'react';
import { motion, useInView } from 'framer-motion';
import { MessageCircle, MapPin, ArrowRight } from 'lucide-react';

const contactItems = [
  { icon: MessageCircle, label: 'WhatsApp', value: 'Chat with us', href: 'https://wa.me/919988778877', color: '#25D366' },
  { icon: MapPin,        label: 'Location', value: 'India',        href: null,                        color: 'var(--amber)' },
];

export default function Contact() {
  const ref = useRef(null);
  const inView = useInView(ref, { once: true, margin: '-80px' });

  return (
    <section id="contact" ref={ref} style={{ padding: '100px 5%', background: 'var(--navy-mid)', position: 'relative', overflow: 'hidden' }}>
      <div style={{ position: 'absolute', top: 0, left: 0, right: 0, height: 1, background: 'linear-gradient(90deg, transparent, rgba(0,172,193,0.22), transparent)' }} />

      {/* Ambient glows */}
      <div style={{ position: 'absolute', top: '20%', right: '5%', width: 350, height: 350, borderRadius: '50%', background: 'radial-gradient(circle, rgba(0,172,193,0.08) 0%, transparent 70%)' }} />
      <div style={{ position: 'absolute', bottom: '10%', left: '5%', width: 300, height: 300, borderRadius: '50%', background: 'radial-gradient(circle, rgba(217,122,0,0.06) 0%, transparent 70%)' }} />

      <div style={{ maxWidth: 1280, margin: '0 auto', position: 'relative', zIndex: 1 }}>
        <motion.div initial={{ opacity: 0, y: 30 }} animate={inView ? { opacity: 1, y: 0 } : {}} transition={{ duration: 0.7 }}
          style={{ textAlign: 'center', marginBottom: 72 }}>
          <span style={{ fontFamily: 'Space Mono', fontSize: 11, letterSpacing: '0.25em', color: 'var(--teal)', textTransform: 'uppercase' }}>
            — Get in Touch
          </span>
          <h2 style={{ fontSize: 'clamp(36px, 4vw, 56px)', fontWeight: 700, color: 'var(--text-main)', marginTop: 16, lineHeight: 1.1 }}>
            Let's Start a Conversation
          </h2>
          <p style={{ color: 'var(--text-muted)', fontSize: 17, marginTop: 16, maxWidth: 520, margin: '16px auto 0', fontWeight: 300, lineHeight: 1.7 }}>
            Have a project in mind, need a security review, or simply want to explore what's possible? Reach out — we respond fast.
          </p>
        </motion.div>

        {/* Main CTA card */}
        <motion.div initial={{ opacity: 0, y: 40 }} animate={inView ? { opacity: 1, y: 0 } : {}} transition={{ duration: 0.7, delay: 0.15 }}
          style={{
            background: 'linear-gradient(135deg, #ffffff, #f4fbfc)',
            border: '1px solid rgba(0,172,193,0.16)',
            borderRadius: 16, padding: 'clamp(40px, 6%, 72px)',
            display: 'grid', gridTemplateColumns: '1.2fr 1fr', gap: 60,
            alignItems: 'center', marginBottom: 40, position: 'relative', overflow: 'hidden',
            boxShadow: 'var(--shadow-soft)',
          }} className="contact-main">

          {/* Decorative top-left stripe */}
          <div style={{ position: 'absolute', top: 0, left: 0, right: 0, height: 3, background: 'linear-gradient(90deg, var(--teal), var(--amber), transparent)' }} />

          <div>
            <h3 style={{ fontFamily: 'Rajdhani', fontWeight: 700, fontSize: 'clamp(28px, 3vw, 40px)', color: 'var(--text-main)', lineHeight: 1.1, marginBottom: 20 }}>
              Ready to work with a team that puts <span style={{ color: 'var(--teal-dark)' }}>reliability first</span>?
            </h3>
            <p style={{ color: 'var(--text-muted)', fontSize: 16, lineHeight: 1.8, fontWeight: 300, marginBottom: 36 }}>
              Drop us a message on WhatsApp and one of our consultants will get back to you — usually within the hour on business days.
            </p>
            <a href="https://wa.me/919988778877" target="_blank" rel="noreferrer"
              style={{
                display: 'inline-flex', alignItems: 'center', gap: 12,
                background: '#25D366', color: '#fff',
                fontFamily: 'Rajdhani', fontWeight: 700, fontSize: 16,
                letterSpacing: '0.06em', textTransform: 'uppercase',
                padding: '16px 36px', borderRadius: 6,
                boxShadow: '0 4px 24px rgba(37,211,102,0.35)',
                transition: 'transform 0.2s, box-shadow 0.2s',
              }}
              onMouseEnter={e => { e.currentTarget.style.transform = 'translateY(-2px)'; e.currentTarget.style.boxShadow = '0 8px 36px rgba(37,211,102,0.5)'; }}
              onMouseLeave={e => { e.currentTarget.style.transform = 'translateY(0)'; e.currentTarget.style.boxShadow = '0 4px 24px rgba(37,211,102,0.35)'; }}>
              <MessageCircle size={20} />
              Chat on WhatsApp
              <ArrowRight size={16} />
            </a>
          </div>

          {/* Contact details */}
          <div style={{ display: 'flex', flexDirection: 'column', gap: 24 }}>
            {contactItems.map((c, i) => {
              const Icon = c.icon;
              const inner = (
                <motion.div
                  key={c.label}
                  initial={{ opacity: 0, x: 30 }}
                  animate={inView ? { opacity: 1, x: 0 } : {}}
                  transition={{ duration: 0.6, delay: 0.3 + i * 0.1 }}
                  style={{
                    display: 'flex', alignItems: 'center', gap: 16,
                    background: '#ffffff',
                    border: '1px solid var(--navy-border)',
                    borderRadius: 8, padding: '20px 24px',
                    boxShadow: '0 14px 36px rgba(15,23,42,0.05)',
                    transition: 'border-color 0.2s, background 0.2s',
                    cursor: c.href ? 'pointer' : 'default',
                  }}
                  whileHover={c.href ? { scale: 1.02 } : {}}>
                  <div style={{ width: 44, height: 44, borderRadius: 10, background: `${c.color}18`, display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
                    <Icon size={20} color={c.color} />
                  </div>
                  <div>
                    <div style={{ fontFamily: 'Space Mono', fontSize: 10, letterSpacing: '0.15em', color: 'var(--text-muted)', textTransform: 'uppercase', marginBottom: 4 }}>{c.label}</div>
                    <div style={{ fontFamily: 'Rajdhani', fontWeight: 600, fontSize: 17, color: 'var(--text-main)' }}>{c.value}</div>
                  </div>
                </motion.div>
              );
              return c.href ? <a key={c.label} href={c.href} target="_blank" rel="noreferrer" style={{ textDecoration: 'none', display: 'block' }}>{inner}</a> : inner;
            })}
          </div>
        </motion.div>
      </div>

      <style>{`
        @media (max-width: 900px) {
          .contact-main { grid-template-columns: 1fr !important; }
        }
      `}</style>
    </section>
  );
}
